import React, { useState } from 'react';
import { dashboardAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { bdaOptions } from '../utils/bdeOwnership';
import { Link2, X } from 'lucide-react';

/**
 * "The client booked it and sent us a link."
 *
 * The scheduler next door creates the meeting in our own Outlook and hands back
 * a Teams link. This is the other half: sometimes the client books, and all we
 * have is the link in their email. That meeting still has to appear on the
 * schedule, so this records it WITHOUT creating a calendar event of ours —
 * putting a second, competing invitation in front of a client who has already
 * sent one is worse than having no calendar entry.
 *
 * A lead is optional here for the same reason: a client can book before the
 * lead record catches up, and the company name is enough to keep the row.
 */
const MeetingLinkForm = ({ leadOptions = [], roster = [], onSaved, onCancel }) => {
  const { user } = useAuth();
  const [form, setForm] = useState({
    lead_id: '', company_name: '', poc_name: '', country: '',
    date: '', time: '', duration_minutes: 30,
    join_url: '', bde_name: user?.name || '', shree_time: '', email_note: '', meeting_title: ''
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  // Picking the lead fills in what we already know, so nobody retypes it.
  const pickLead = (id) => {
    const lead = leadOptions.find(l => String(l.id) === String(id));
    setForm(f => ({
      ...f,
      lead_id: id,
      company_name: lead?.company_name || f.company_name,
      poc_name: lead?.poc_name || lead?.poc1_name || f.poc_name,
      country: lead?.country || f.country,
      bde_name: lead?.bde_name || f.bde_name
    }));
  };

  const save = async () => {
    setError('');
    if (!form.company_name.trim()) return setError('Which company is the meeting with?');
    if (!form.date) return setError('Pick the date.');
    if (!form.time) return setError('Pick the time.');
    // Typed as a wall clock in IST, which is the zone the schedule is kept in.
    const scheduled_at = new Date(`${form.date}T${form.time}:00`).toISOString();
    setSaving(true);
    try {
      const res = await dashboardAPI.createMeeting({
        ...form,
        lead_id: form.lead_id || null,
        link_source: 'client',          // the server will not book an Outlook event
        create_calendar_event: false,
        scheduled_at,
        time_zone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        meeting_title: form.meeting_title || `Meeting with ${form.company_name.trim()}`,
        userId: user?.id,
        userEmail: user?.email
      });
      if (res.success) { onSaved?.(res.data); }
      else setError(res.error || 'Could not save.');
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setSaving(false);
    }
  };

  const field = 'border border-gray-300 rounded-lg px-3 py-2 text-sm w-full';
  const label = 'block text-xs font-medium text-gray-600 mb-1';

  return (
    <div className="bg-white rounded-lg border-2 border-[#0f6cbd] p-4 space-y-3">
      <div className="flex items-center gap-2">
        <Link2 size={18} className="text-[#0f6cbd]" />
        <h3 className="text-sm font-semibold text-gray-900">Add a meeting the client booked</h3>
        <span className="text-xs text-gray-500">no invitation is sent — we just record their link</span>
        <button type="button" onClick={onCancel} className="ml-auto p-1 text-gray-400 hover:text-gray-700" title="Close">
          <X size={16} />
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className="md:col-span-1">
          <label className={label}>Lead (optional)</label>
          <select value={form.lead_id} onChange={(e) => pickLead(e.target.value)} className={field}>
            <option value="">— not linked to a lead —</option>
            {leadOptions.map(l => <option key={l.id} value={l.id}>{l.company_name}</option>)}
          </select>
        </div>
        <div>
          <label className={label}>Company *</label>
          <input className={field} value={form.company_name} onChange={(e) => set('company_name', e.target.value)} placeholder="Renexia" />
        </div>
        <div>
          <label className={label}>POC name</label>
          <input className={field} value={form.poc_name} onChange={(e) => set('poc_name', e.target.value)} placeholder="Simon Anderson" />
        </div>
        <div>
          <label className={label}>Country</label>
          <input className={field} value={form.country} onChange={(e) => set('country', e.target.value)} placeholder="Italy" />
        </div>
        <div>
          <label className={label}>Date *</label>
          <input type="date" className={field} value={form.date} onChange={(e) => set('date', e.target.value)} />
        </div>
        <div>
          <label className={label}>Time *</label>
          <input type="time" className={field} value={form.time} onChange={(e) => set('time', e.target.value)} />
        </div>
        <div>
          <label className={label}>BDA</label>
          <select className={field} value={form.bde_name} onChange={(e) => set('bde_name', e.target.value)}>
            <option value="">—</option>
            {bdaOptions(roster).map(b => <option key={b.id} value={b.name}>{b.name}</option>)}
          </select>
        </div>
        <div>
          <label className={label}>Shree (IST)</label>
          <input className={field} value={form.shree_time} onChange={(e) => set('shree_time', e.target.value)} placeholder="Available / 1pm or after 4pm" />
        </div>
        <div>
          <label className={label}>Duration (min)</label>
          <input type="number" min="5" step="5" className={field} value={form.duration_minutes} onChange={(e) => set('duration_minutes', e.target.value)} />
        </div>
        <div className="md:col-span-2">
          <label className={label}>Meeting link the client sent</label>
          <input className={field} value={form.join_url} onChange={(e) => set('join_url', e.target.value)}
            placeholder="https://teams.microsoft.com/... or a Zoom / Meet link" />
        </div>
        <div>
          <label className={label}>Note</label>
          <input className={field} value={form.email_note} onChange={(e) => set('email_note', e.target.value)} placeholder="Sent - Surabhi" />
        </div>
      </div>

      {error && <p className="text-sm text-red-700 bg-red-50 rounded px-3 py-2">{error}</p>}

      <div className="flex items-center gap-2">
        <button type="button" onClick={save} disabled={saving}
          className="px-4 py-2 bg-[#0f6cbd] text-white rounded-lg text-sm font-medium hover:bg-[#0c5a9e] disabled:opacity-50">
          {saving ? 'Saving…' : 'Add to the schedule'}
        </button>
        <button type="button" onClick={onCancel} className="px-4 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50">
          Cancel
        </button>
      </div>
    </div>
  );
};

export default MeetingLinkForm;
